import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  Server, Database, HardDrive, Cpu, Activity, RefreshCw, 
  CheckCircle, AlertTriangle, XCircle, Clock, Zap, 
  BarChart3, TrendingUp, Box, Layers
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ServerHealthDashboard() {
  const { token } = useAuth();
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchSystemStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/system-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch system status');
      }
      
      const data = await response.json();
      setSystemStatus(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSystemStatus();
    
    // Auto-refresh every 30 seconds if enabled
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchSystemStatus, 30000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchSystemStatus, autoRefresh]);

  const getStatusColor = (status) => {
    if (status === 'healthy' || status === true || status === 'running') return 'text-green-500';
    if (status === 'warning' || status === 'degraded') return 'text-amber-500';
    return 'text-red-500';
  };

  const getStatusIcon = (status) => {
    if (status === 'healthy' || status === true || status === 'running') 
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    if (status === 'warning' || status === 'degraded') 
      return <AlertTriangle className="h-5 w-5 text-amber-500" />;
    return <XCircle className="h-5 w-5 text-red-500" />;
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-orange-500" />
        <span className="ml-2 text-slate-600">Loading system status...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <XCircle className="h-6 w-6 text-red-500" />
              <div>
                <h3 className="font-semibold text-red-800">Error Loading System Status</h3>
                <p className="text-red-600">{error}</p>
              </div>
            </div>
            <Button onClick={fetchSystemStatus} className="mt-4" variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" /> Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="server-health-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Server className="h-7 w-7 text-orange-500" />
            Server Health Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Monitor system performance and resource usage</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            data-testid="auto-refresh-toggle"
          >
            <Activity className={`h-4 w-4 mr-2 ${autoRefresh ? 'animate-pulse' : ''}`} />
            {autoRefresh ? 'Auto-Refresh ON' : 'Auto-Refresh OFF'}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchSystemStatus} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Last Updated */}
      {lastUpdated && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Clock className="h-4 w-4" />
          Last updated: {lastUpdated.toLocaleTimeString()}
        </div>
      )}

      {/* Overall Status Banner */}
      <Card className={`${systemStatus?.storage?.type ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            {systemStatus?.storage?.type ? (
              <>
                <CheckCircle className="h-8 w-8 text-green-500" />
                <div>
                  <h3 className="font-semibold text-green-800">All Systems Operational</h3>
                  <p className="text-green-600">Server is healthy and responding normally</p>
                </div>
              </>
            ) : (
              <>
                <AlertTriangle className="h-8 w-8 text-amber-500" />
                <div>
                  <h3 className="font-semibold text-amber-800">Partial Data Available</h3>
                  <p className="text-amber-600">Some metrics may be unavailable</p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Storage Status */}
        <Card data-testid="storage-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <HardDrive className="h-4 w-4 text-blue-500" />
              Storage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold capitalize">
              {systemStatus?.storage?.type || 'Unknown'}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {systemStatus?.storage?.gcs_configured 
                ? 'GCS Configured' 
                : 'Local Storage (GCS not configured)'}
            </p>
            <Badge 
              variant={systemStatus?.storage?.gcs_available ? 'default' : 'secondary'}
              className="mt-2"
            >
              {systemStatus?.storage?.gcs_available ? 'GCS Available' : 'GCS Not Installed'}
            </Badge>
          </CardContent>
        </Card>

        {/* Database Status */}
        <Card data-testid="database-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Database className="h-4 w-4 text-green-500" />
              Database
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {systemStatus?.database?.connections || 0}
            </div>
            <p className="text-xs text-slate-500 mt-1">Active Connections</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline">
                MongoDB {systemStatus?.database?.version?.split('.').slice(0,2).join('.') || 'N/A'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Background Executor */}
        <Card data-testid="executor-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Cpu className="h-4 w-4 text-purple-500" />
              Background Workers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {systemStatus?.background_executor?.max_workers || 0}
            </div>
            <p className="text-xs text-slate-500 mt-1">Max Workers (ThreadPool)</p>
            <Badge variant="default" className="mt-2 bg-purple-500">
              Ready for PDF/Heavy Tasks
            </Badge>
          </CardContent>
        </Card>

        {/* AEAT Connector */}
        <Card data-testid="aeat-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Zap className="h-4 w-4 text-orange-500" />
              AEAT Connector
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {getStatusIcon(systemStatus?.aeat_connector?.running)}
              <span className={`text-lg font-bold ${getStatusColor(systemStatus?.aeat_connector?.running)}`}>
                {systemStatus?.aeat_connector?.running ? 'Running' : 'Stopped'}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {systemStatus?.aeat_connector?.configured 
                ? 'Certificate Configured' 
                : 'Awaiting Certificate'}
            </p>
            <Badge 
              variant={systemStatus?.aeat_connector?.sandbox_mode ? 'secondary' : 'default'}
              className="mt-2"
            >
              {systemStatus?.aeat_connector?.sandbox_mode ? 'Sandbox Mode' : 'Production'}
            </Badge>
          </CardContent>
        </Card>
      </div>

      {/* Cache Statistics */}
      <Card data-testid="cache-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-indigo-500" />
            Cache Statistics
          </CardTitle>
          <CardDescription>
            In-memory TTL caches for optimized data access
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {systemStatus?.cache && Object.entries(systemStatus.cache).map(([key, value]) => {
              const percentage = value.maxsize > 0 ? (value.size / value.maxsize) * 100 : 0;
              return (
                <div key={key} className="p-4 bg-slate-50 rounded-lg" data-testid={`cache-${key}`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-sm capitalize">
                      {key.replace(/_/g, ' ')}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      TTL: {value.ttl}s
                    </Badge>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold">{value.size}</span>
                    <span className="text-slate-500 text-sm">/ {value.maxsize}</span>
                  </div>
                  <Progress value={percentage} className="mt-2 h-2" />
                  <p className="text-xs text-slate-500 mt-1">
                    {percentage.toFixed(1)}% utilized
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* System Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Connection Pool Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Box className="h-5 w-5 text-cyan-500" />
              Connection Pool Configuration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded">
                <span className="text-slate-600">Max Pool Size</span>
                <span className="font-bold">100</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded">
                <span className="text-slate-600">Min Pool Size</span>
                <span className="font-bold">10</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded">
                <span className="text-slate-600">Idle Timeout</span>
                <span className="font-bold">30s</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded">
                <span className="text-slate-600">Retry Writes/Reads</span>
                <span className="font-bold text-green-600">Enabled</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* AEAT Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-orange-500" />
              AEAT Submission Queue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded">
                <span className="text-slate-600">Processor Status</span>
                <Badge variant={systemStatus?.aeat_connector?.running ? 'default' : 'secondary'}>
                  {systemStatus?.aeat_connector?.running ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded">
                <span className="text-slate-600">Certificate</span>
                <Badge variant={systemStatus?.aeat_connector?.configured ? 'default' : 'destructive'}>
                  {systemStatus?.aeat_connector?.configured ? 'Loaded' : 'Not Configured'}
                </Badge>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded">
                <span className="text-slate-600">Max Retries</span>
                <span className="font-bold">{systemStatus?.aeat_connector?.max_retries || 5}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded">
                <span className="text-slate-600">Environment</span>
                <Badge variant={systemStatus?.aeat_connector?.sandbox_mode ? 'secondary' : 'default'}>
                  {systemStatus?.aeat_connector?.sandbox_mode ? 'Sandbox' : 'Production'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Timestamp */}
      <div className="text-center text-xs text-slate-400">
        Server time: {systemStatus?.timestamp ? new Date(systemStatus.timestamp).toLocaleString() : 'N/A'}
      </div>
    </div>
  );
}
